import { Link, Navigate, useNavigate, useParams } from 'react-router'
import { ArrowLeft } from 'lucide-react'
import { uuidParamSchema } from '@lib/validators/params'
import { useAuth } from '@/auth/AuthContext'
import { PageHeader } from '@/components/PageHeader'
import { ErrorState, LoadingState } from '@/components/PageState'
import { TenantForm } from '@/components/tenants/TenantForm'
import { toast } from '@/components/ui/toast'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { useOpsTenant, useOpsTenantActions } from '@/hooks/useOpsTenants'

/** Staff ADMIN editing a tenant's name, sender identity, and branding. */
export function OpsTenantEditPage() {
  const { id: rawId } = useParams()
  const parsedId = uuidParamSchema.safeParse(rawId)
  const id = parsedId.success ? parsedId.data : null
  const { user } = useAuth()
  const navigate = useNavigate()
  const query = useOpsTenant(id)
  const { update } = useOpsTenantActions(id)

  if (id === null) {
    return (
      <>
        <PageHeader title="Edit tenant" />
        <ErrorState message="That organization link is not valid." />
      </>
    )
  }

  const detailPath = `/ops/tenants/${id}`

  if (user?.role !== 'ADMIN') return <Navigate to={detailPath} replace />

  const backLink = (
    <Link to={detailPath} className="inline-flex items-center gap-1 hover:text-foreground">
      <ArrowLeft className="size-3.5" />
      {query.data?.name ?? 'Tenant'}
    </Link>
  )

  function renderBody() {
    if (query.isLoading) return <LoadingState />
    if (query.isError || !query.data) {
      return <ErrorState message={apiErrorMessage(query.error, 'Could not load the tenant.')} />
    }
    return (
      <TenantForm
        mode="edit"
        initial={query.data}
        cancelTo={detailPath}
        onSubmit={async (input) => {
          await update.mutateAsync(input)
          toast.success('Changes saved')
          navigate(detailPath)
        }}
      />
    )
  }

  return (
    <>
      <PageHeader eyebrow={backLink} title="Edit tenant" />
      {renderBody()}
    </>
  )
}
